/**
 * Email Notification Service for Front Desk Early Dismissal
 * 
 * Handles:
 * - 3-email sequence (immediate, +10min, +20min) via Resend API proxy
 * - Parallel Camper Snapshot webhook calls
 * - localStorage persistence for re-queuing on page refresh
 * - Cancellation and restart logic
 */

import { getHeadCounselorEmail } from './headCounselorEmails';

const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' && !window.location.hostname.includes('preview.emergentagent.com') ? `${window.location.origin}/api` : `${process.env.REACT_APP_BACKEND_URL}/api`;
const safeFetch = (...args) => (typeof window !== 'undefined' && window.__origFetch ? window.__origFetch : fetch)(...args);
const WEBHOOK_URL = `${API}/summer2026/snapshot/notify`;
const FROM_EMAIL = 'attendance@campersnapshot.com';
const TEN_MINUTES = 10 * 60 * 1000;
const TWENTY_MINUTES = 20 * 60 * 1000;

// In-memory timer references (not persisted, re-created on load)
const _timers = {};

const DEFAULT_RESEND_KEY = 're_BeuYp4FZ_FaMNTJZzXqTFvPHX7dcKf2GU';

function getResendApiKey() {
  return localStorage.getItem('resend_api_key') || DEFAULT_RESEND_KEY;
}

function getNotificationKey(personId, date) {
  return `notification_${personId}_${date}_pickup`;
}

function getNotificationState(personId, date) {
  try {
    const raw = localStorage.getItem(getNotificationKey(personId, date));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function setNotificationState(personId, date, state) {
  localStorage.setItem(getNotificationKey(personId, date), JSON.stringify(state));
}

function clearNotificationState(personId, date) {
  localStorage.removeItem(getNotificationKey(personId, date));
}

function buildEmailBody(camper, groupInfo, pickupTime, dateStr, reminderNum) {
  const camperName = `${camper.firstName} ${camper.lastName}`;
  const groupLabel = `${groupInfo.name} (${camper.bunkCode})`;

  if (reminderNum === 1) {
    return `Rolling River Day Camp — Early Dismissal Notice

Camper: ${camperName}
Group: ${groupLabel}
Date: ${dateStr}

The parent or guardian of ${camperName} has been called or emailed and is expected to arrive at ${pickupTime}.

Please bring ${camper.firstName} to the front of camp by ${pickupTime}.

Thank you,
Rolling River Attendance System`;
  }

  if (reminderNum === 2) {
    return `Rolling River Day Camp — Early Dismissal Reminder

REMINDER: This is a follow-up to a notice sent 10 minutes ago.

Camper: ${camperName}
Group: ${groupLabel}
Date: ${dateStr}

The parent or guardian of ${camperName} has been called or emailed and is expected to arrive at ${pickupTime}.

Please bring ${camper.firstName} to the front of camp by ${pickupTime}.

Thank you,
Rolling River Attendance System`;
  }

  return `Rolling River Day Camp — Early Dismissal Final Reminder

FINAL REMINDER: This is the third and final notice. Originally sent 20 minutes ago.

Camper: ${camperName}
Group: ${groupLabel}
Date: ${dateStr}

The parent or guardian of ${camperName} has been called or emailed and is expected to arrive at ${pickupTime}.

Please bring ${camper.firstName} to main office by ${pickupTime}.

Thank you,
Rolling River Attendance System`;
}

function buildSubject(camper, reminderNum) {
  const name = `${camper.firstName} ${camper.lastName}`;
  if (reminderNum === 1) return `Early Dismissal: ${name}`;
  if (reminderNum === 2) return `(Reminder) Early Dismissal: ${name}`;
  return `(Final Reminder) Early Dismissal: ${name}`;
}

// Send email via backend proxy (avoids CORS)
async function sendResendEmail(to, subject, body, apiKey) {
  const res = await safeFetch(`${API}/send-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      apiKey,
      from: FROM_EMAIL,
      to,
      subject,
      text: body,
    }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Email send failed');
  return data;
}

// Fire webhook (parallel, silent failures)
async function fireWebhook(payload) {
  try {
    await safeFetch(WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    const key = `webhook_errors_${new Date().toISOString().split('T')[0]}`;
    const errors = JSON.parse(localStorage.getItem(key) || '[]');
    errors.push({ time: new Date().toISOString(), error: err.message, payload: payload.camperName });
    localStorage.setItem(key, JSON.stringify(errors));
  }
}

// Send one email + webhook in parallel
async function sendNotification(camper, groupInfo, pickupTime, dateStr, dismissalTime, reminderNum, onStatus) {
  const apiKey = getResendApiKey();
  const to = groupInfo.email;
  const subject = buildSubject(camper, reminderNum);
  const body = buildEmailBody(camper, groupInfo, pickupTime, dateStr, reminderNum);

  const webhookPayload = {
    to,
    subject,
    body,
    camperName: `${camper.firstName} ${camper.lastName}`,
    groupCode: camper.bunkCode,
    groupName: groupInfo.name,
    eventType: 'early_dismissal',
    dismissalTime: dismissalTime || '',
    pickupArrivalTime: pickupTime,
    date: dateStr,
    reminderNumber: reminderNum,
  };

  // Fire both in parallel — independent of each other
  const emailPromise = sendResendEmail(to, subject, body, apiKey)
    .then(() => {
      if (onStatus) onStatus({ type: 'email_success', reminderNum });
      return true;
    })
    .catch((err) => {
      if (onStatus) onStatus({ type: 'email_failure', reminderNum, error: err.message });
      return false;
    });

  // Webhook fires independently — no UI error on failure
  fireWebhook(webhookPayload);

  return emailPromise;
}

/**
 * Start the 3-email notification sequence.
 * @param {Object} camper - { personId, firstName, lastName, bunkCode, bunkName }
 * @param {string} pickupTime - e.g. "2:30 PM"
 * @param {string} dismissalTime - existing dismissal time (if any)
 * @param {string} date - ISO date e.g. "2026-06-29"
 * @param {Function} onStatus - callback for UI updates
 * @returns {boolean} false if Resend key is missing
 */
export function startNotificationSequence(camper, pickupTime, dismissalTime, date, onStatus) {
  const apiKey = getResendApiKey();
  if (!apiKey) {
    if (onStatus) onStatus({ type: 'no_api_key' });
    return false;
  }

  const personId = camper.personId;
  const groupInfo = getHeadCounselorEmail(camper.bunkCode);
  const dateStr = new Date(date + 'T12:00:00').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  // Cancel any existing timers for this camper
  cancelPendingReminders(personId, date);

  const now = Date.now();
  const state = {
    originalTimestamp: now,
    email1Sent: false,
    email2Sent: false,
    email3Sent: false,
    pickupTime,
    camperFirstName: camper.firstName,
    camperLastName: camper.lastName,
    bunkCode: camper.bunkCode,
    bunkName: camper.bunkName,
    dismissalTime: dismissalTime || '',
    date,
  };
  setNotificationState(personId, date, state);

  // Email 1: Immediate
  sendNotification(camper, groupInfo, pickupTime, dateStr, dismissalTime, 1, onStatus)
    .then((success) => {
      if (success) {
        const s = getNotificationState(personId, date);
        if (s) { s.email1Sent = true; setNotificationState(personId, date, s); }
      }
    });

  // Email 2: +10 minutes
  const timer2 = setTimeout(() => {
    const s = getNotificationState(personId, date);
    if (!s) return; // Cancelled
    sendNotification(camper, groupInfo, pickupTime, dateStr, dismissalTime, 2, onStatus)
      .then((success) => {
        if (success) {
          const s2 = getNotificationState(personId, date);
          if (s2) { s2.email2Sent = true; setNotificationState(personId, date, s2); }
        }
      });
  }, TEN_MINUTES);

  // Email 3: +20 minutes
  const timer3 = setTimeout(() => {
    const s = getNotificationState(personId, date);
    if (!s) return; // Cancelled
    sendNotification(camper, groupInfo, pickupTime, dateStr, dismissalTime, 3, onStatus)
      .then((success) => {
        if (success) {
          const s2 = getNotificationState(personId, date);
          if (s2) {
            s2.email3Sent = true;
            setNotificationState(personId, date, s2);
            // All sent — clean up after a brief delay
            setTimeout(() => clearNotificationState(personId, date), 5000);
          }
        }
      });
  }, TWENTY_MINUTES);

  _timers[`${personId}_${date}`] = [timer2, timer3];
  return true;
}

/**
 * Cancel any pending reminder timers for a camper/date.
 */
export function cancelPendingReminders(personId, date) {
  const key = `${personId}_${date}`;
  if (_timers[key]) {
    _timers[key].forEach(t => clearTimeout(t));
    delete _timers[key];
  }
  clearNotificationState(personId, date);
}

/**
 * Re-queue pending reminders on app load.
 * Scans localStorage for notification_*_*_pickup entries and re-schedules unsent emails.
 * @param {Function} onStatus - callback for UI updates
 */
export function rehydrateNotifications(onStatus) {
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith('notification_') && k.endsWith('_pickup')) {
      keys.push(k);
    }
  }

  keys.forEach(k => {
    try {
      const state = JSON.parse(localStorage.getItem(k));
      if (!state || !state.originalTimestamp) return;

      const elapsed = Date.now() - state.originalTimestamp;
      const camper = {
        personId: k.split('_')[1],
        firstName: state.camperFirstName,
        lastName: state.camperLastName,
        bunkCode: state.bunkCode,
        bunkName: state.bunkName,
      };
      const groupInfo = getHeadCounselorEmail(state.bunkCode);
      const dateStr = new Date(state.date + 'T12:00:00').toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
      });
      const personId = camper.personId;

      // Email 2: should fire at +10min
      if (!state.email2Sent) {
        const remaining2 = TEN_MINUTES - elapsed;
        if (remaining2 <= 0) {
          // Overdue — fire immediately
          sendNotification(camper, groupInfo, state.pickupTime, dateStr, state.dismissalTime, 2, onStatus)
            .then(success => {
              if (success) {
                const s = getNotificationState(personId, state.date);
                if (s) { s.email2Sent = true; setNotificationState(personId, state.date, s); }
              }
            });
        } else {
          const timer2 = setTimeout(() => {
            const s = getNotificationState(personId, state.date);
            if (!s) return;
            sendNotification(camper, groupInfo, state.pickupTime, dateStr, state.dismissalTime, 2, onStatus)
              .then(success => {
                if (success) {
                  const s2 = getNotificationState(personId, state.date);
                  if (s2) { s2.email2Sent = true; setNotificationState(personId, state.date, s2); }
                }
              });
          }, remaining2);
          if (!_timers[`${personId}_${state.date}`]) _timers[`${personId}_${state.date}`] = [];
          _timers[`${personId}_${state.date}`].push(timer2);
        }
      }

      // Email 3: should fire at +20min
      if (!state.email3Sent) {
        const remaining3 = TWENTY_MINUTES - elapsed;
        if (remaining3 <= 0) {
          sendNotification(camper, groupInfo, state.pickupTime, dateStr, state.dismissalTime, 3, onStatus)
            .then(success => {
              if (success) {
                const s = getNotificationState(personId, state.date);
                if (s) {
                  s.email3Sent = true;
                  setNotificationState(personId, state.date, s);
                  setTimeout(() => clearNotificationState(personId, state.date), 5000);
                }
              }
            });
        } else {
          const timer3 = setTimeout(() => {
            const s = getNotificationState(personId, state.date);
            if (!s) return;
            sendNotification(camper, groupInfo, state.pickupTime, dateStr, state.dismissalTime, 3, onStatus)
              .then(success => {
                if (success) {
                  const s2 = getNotificationState(personId, state.date);
                  if (s2) {
                    s2.email3Sent = true;
                    setNotificationState(personId, state.date, s2);
                    setTimeout(() => clearNotificationState(personId, state.date), 5000);
                  }
                }
              });
          }, remaining3);
          if (!_timers[`${personId}_${state.date}`]) _timers[`${personId}_${state.date}`] = [];
          _timers[`${personId}_${state.date}`].push(timer3);
        }
      }

      // All 3 already sent — clean up
      if (state.email1Sent && state.email2Sent && state.email3Sent) {
        clearNotificationState(personId, state.date);
      }
    } catch (err) {
      console.error('Rehydrate notification error:', err);
    }
  });
}

/**
 * Get the current notification status for a camper on a date.
 */
export function getNotificationStatus(personId, date) {
  return getNotificationState(personId, date);
}

/**
 * Fire webhook to Camper Snapshot for an early dismissal event.
 * Can be called directly from Front Desk on initial submit.
 */
export function notifySnapshot(payload) {
  fireWebhook(payload);
}

